"use client"

import { FileText, HelpCircle } from "lucide-react"
import DetailsSidePanel from "@/components/shared/DetailsSidePanel"
import { MarkdownRenderer } from "./MarkdownRenderer"
import { useReportPanel } from "./ReportPanelContext"
import { AiWarningBanner } from "./AiWarningBanner"

/**
 * Convert unlinked PMID references into clickable markdown links.
 * The agent writes `[T2: PMID:12345678]` without a URL despite being told
 * to use full links. This function adds the PubMed URL so MarkdownRenderer
 * renders them as clickable.
 *
 * Only processes patterns NOT already followed by `(url)` — so it won't
 * double-link the rare case where the agent does provide the full link.
 *
 * @param text - Markdown text with potential unlinked PMID references.
 * @returns Text with PMID references converted to full markdown links.
 */
function linkPmids(text: string): string {
  if (!text) return ""
  // Match [anything containing PMID:digits] NOT followed by (url)
  return text.replace(
    /\[([^\]]*PMID:(\d+)[^\]]*)\](?!\()/g,
    "[$1](https://pubmed.ncbi.nlm.nih.gov/$2/)"
  )
}

/**
 * Convert markdown text to basic HTML for the print/PDF window.
 * Handles headings, bold, italic, links, lists, and paragraphs.
 * Not a full markdown parser — covers the common patterns the agent produces.
 *
 * @param md - Markdown string to convert.
 * @returns HTML string.
 */
function markdownToSimpleHtml(md: string): string {
  if (!md) return ""
  return md
    // Headings
    .replace(/^#### (.+)$/gm, "<h4>$1</h4>")
    .replace(/^### (.+)$/gm, "<h3>$1</h3>")
    .replace(/^## (.+)$/gm, "<h2>$1</h2>")
    .replace(/^# (.+)$/gm, "<h1>$1</h1>")
    // Bold and italic
    .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" >$1</a>')

    // Unordered lists
    .replace(/^- (.+)$/gm, "<li>$1</li>")
    .replace(/(<li>.*<\/li>\n?)+/g, "<ul>$&</ul>")
    // Line breaks to paragraphs (double newline = paragraph break)
    .replace(/\n\n/g, "</p><p>")
    .replace(/\n/g, "<br>")
    .replace(/^/, "<p>")
    .replace(/$/, "</p>")
    // Clean up empty paragraphs
    .replace(/<p><\/p>/g, "")
    .replace(/<p>(<h[1-4]>)/g, "$1")
    .replace(/(<\/h[1-4]>)<\/p>/g, "$1")
    .replace(/<p>(<ul>)/g, "$1")
    .replace(/(<\/ul>)<\/p>/g, "$1")
}

export function ReportSidePanel() {
  const { report, isOpen, closeReport } = useReportPanel()

  if (!isOpen || !report) return null

  const isFactoid = report.kind === "factoid"
  const Icon = isFactoid ? HelpCircle : FileText

  // Kept for the commented-out Download PDF button below.
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function handleDownloadPdf(): void {
    if (!report) return

    let htmlContent = ""

    if (isFactoid && report.sections) {
      htmlContent = report.sections
        .map(s => `<h2>${s.label}</h2>\n${markdownToSimpleHtml(s.content)}`)
        .join("\n")
    } else {
      htmlContent = [
        `<h1>${report.title}</h1>`,
        `<h2>Executive Summary</h2>`,
        markdownToSimpleHtml(report.summary),
        markdownToSimpleHtml(report.markdown),
      ].join("\n")
    }

    const printWindow = window.open("", "_blank")
    if (!printWindow) return

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Report</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 800px; margin: 40px auto; padding: 0 20px; line-height: 1.6; color: #1a1a1a; }
          h1 { font-size: 1.5em; margin-bottom: 0.5em; }
          h2 { font-size: 1.2em; margin-top: 1.5em; margin-bottom: 0.5em; color: #333; }
          h3 { font-size: 1em; margin-top: 1.2em; margin-bottom: 0.4em; }
          h4 { font-size: 0.9em; margin-top: 1em; margin-bottom: 0.3em; font-weight: 600; }
          p { margin: 0.5em 0; }
          a { color: #2563eb; text-decoration: underline; }
          ul, ol { padding-left: 1.5em; margin: 0.5em 0; }
          li { margin: 0.3em 0; }
          blockquote { border-left: 3px solid #d1d5db; padding-left: 1em; margin: 1em 0; color: #4b5563; }
          code { background: #f3f4f6; padding: 0.1em 0.3em; border-radius: 3px; font-size: 0.9em; }
          pre { background: #f3f4f6; padding: 1em; border-radius: 6px; overflow-x: auto; }
          table { border-collapse: collapse; width: 100%; margin: 1em 0; }
          th, td { border: 1px solid #d1d5db; padding: 0.5em; text-align: left; }
          th { background: #f9fafb; font-weight: 600; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>${htmlContent}</body>
      </html>
    `)
    printWindow.document.close()
    // Small delay to ensure styles are applied before print dialog opens
    setTimeout(() => printWindow.print(), 250)
  }

  return (
    <DetailsSidePanel
      onClose={closeReport}
      widthClass="w-full"
      header={
        <div className="flex items-center gap-2">
          <Icon className="h-4 mb-1 w-4 text-primary shrink-0" />
          <h2 className="text-sm font-semibold">{report.title}</h2>
        </div>
      }
    >
      <div className="flex-1 p-4 space-y-4">
        <AiWarningBanner />

        {/*<div className="flex justify-start">*/}
        {/*  <Button*/}
        {/*    variant="outline"*/}
        {/*    size="sm"*/}
        {/*    onClick={handleDownloadPdf}*/}
        {/*    className="gap-1.5 text-xs"*/}
        {/*  >*/}
        {/*    <Download className="h-3.5 w-3.5" />*/}
        {/*    Download PDF*/}
        {/*  </Button>*/}
        {/*</div>*/}

        {isFactoid && report.sections ? (
          report.sections.map((section, idx) => (
            <div key={idx} className="border-l-2 border-primary/30 pl-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                {section.label}
              </p>
              <div className="text-sm text-foreground prose prose-sm max-w-none [&_a]:text-primary [&_a]:underline [&_a]:hover:text-primary/80">
                <MarkdownRenderer content={linkPmids(section.content)} />
              </div>
            </div>
          ))
        ) : (
          <>
            <h1 className="text-lg font-semibold text-foreground">{report.title}</h1>

            <div className="border-l-2 border-primary/30 pl-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Executive Summary
              </p>
              <div className="text-sm text-foreground prose prose-sm max-w-none [&_a]:text-primary [&_a]:underline [&_a]:hover:text-primary/80">
                <MarkdownRenderer content={linkPmids(report.summary)} />
              </div>
            </div>

            <div className="prose prose-sm max-w-none text-foreground [&_a]:text-primary [&_a]:underline [&_a]:hover:text-primary/80">
              <MarkdownRenderer content={linkPmids(report.markdown)} />
            </div>
          </>
        )}
      </div>
    </DetailsSidePanel>
  )
}
